import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { isEqual } from 'lodash-es';
import { Subject, finalize, switchMap, takeUntil } from 'rxjs';
import { RktFormModule, formInputPassword, formRow } from '@rocketfinancialcorp/rocket-ui/form';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';

import { PasswordStrengthComponent } from '@shared/components';
import { MESSAGE, MIN_PASSWORD_LENGTH } from '@shared/constants';
import { AuthService } from '@shared/services';
import { fromAuth } from '@shared/store';

interface ChangePasswordModel {
  oldPassword: string;
  password: string;
  passwordConfirmation: string;
}

@Component({
  selector: 'app-change-password',
  templateUrl: 'change-password.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, ReactiveFormsModule, RktFormModule, PasswordStrengthComponent],
})
export class ChangePasswordComponent implements AfterViewChecked, OnDestroy {
  changePasswordForm = new FormGroup({});

  changePasswordModel: Partial<ChangePasswordModel> = {};

  error?: string;

  loading!: boolean;

  minLength = MIN_PASSWORD_LENGTH;

  passwordStrength = '';

  isAllChecksPass = false;

  changePasswordFields: FormlyFieldConfig[] = [
    formRow([
      formInputPassword({
        key: 'oldPassword',
        label: 'Current password',
        props: {
          required: true,
          type: 'password',
        },
      }),
    ]),
    formRow([
      formInputPassword({
        key: 'password',
        label: 'New password',
        props: {
          required: true,
          type: 'password',
        },
      }),
    ]),
    formRow([
      formInputPassword({
        key: 'passwordConfirmation',
        label: 'Confirm new password',
        props: {
          required: true,
          type: 'password',
        },
        expressions: {
          'props.disabled': '!model.password',
        },
      }),
    ]),
  ];

  get isDataUnchanged() {
    return isEqual({ ...this.changePasswordModel }, this.originalData);
  }

  originalData: Partial<ChangePasswordModel> = {
    oldPassword: '',
    password: '',
    passwordConfirmation: '',
  };

  private destroy$ = new Subject<void>();

  private userEmail?: string;

  constructor(
    private ref: ChangeDetectorRef,
    public activeModal: ActiveModal,
    private authService: AuthService,
    private store: Store,
  ) {
    this.changePasswordModel = { ...this.changePasswordModel, ...this.originalData };

    this.store
      .select(fromAuth.selectUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => (this.userEmail = user?.email));
  }

  canDeactivate(): boolean {
    return this.isDataUnchanged;
  }

  ngAfterViewChecked(): void {
    this.ref.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSubmit(): void {
    this.changePasswordForm.markAllAsTouched();
    if (this.changePasswordForm.invalid) {
      return;
    }

    const { oldPassword, password, passwordConfirmation } = this.changePasswordModel;

    if (!oldPassword || !password || !passwordConfirmation) {
      return;
    }

    if (!this.userEmail) {
      this.error = MESSAGE.UNKNOWN_ERROR;
      return;
    }

    this.error = undefined;
    this.loading = true;

    // PCI remediation: re-authenticate with the current password to obtain a fresh provider token,
    // then submit the change without ever sending the current password to the backend.
    this.authService
      .getReauthProviderToken(this.userEmail, oldPassword)
      .pipe(
        switchMap((providerToken) =>
          this.authService.changePassword({
            newPassword: password,
            newPasswordConfirmation: passwordConfirmation,
            providerToken,
          }),
        ),
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: () => this.activeModal.close(true),
        error: (error) => {
          this.error = typeof error === 'string' ? error : (error?.message ?? MESSAGE.UNKNOWN_ERROR);
        },
      });
  }

  updatePasswordStrength({ strengthString, isAllChecksPass }: { strengthString: string; isAllChecksPass: boolean }): void {
    this.passwordStrength = strengthString;
    this.isAllChecksPass = isAllChecksPass;
  }

  onPasswordChange(): void {
    const { password, passwordConfirmation } = this.changePasswordModel || {};

    const formEl = this.changePasswordForm.get('passwordConfirmation')!;

    formEl.setErrors(null);

    if (password !== passwordConfirmation) {
      formEl.setErrors({ matchError: { message: 'Password does not match' } });
    }
  }
}
